/**
 * Enhanced Infinite Scroll for Shopify Dawn Theme
 * Features:
 * - Smooth loading of products as user scrolls
 * - Maintains browser history with pushState
 * - Efficient scroll event handling with debounce
 * - Preserves all product card functionality
 * - Shows end message when all products are loaded
 */
class EnhancedInfiniteScroll {
  constructor() {
    this.productGrid = document.getElementById('product-grid');
    this.productGridContainer = document.getElementById('ProductGridContainer');
    this.isLoading = false;
    this.currentPage = 1;
    this.paginationContainer = document.querySelector('.pagination-container');
    this.totalPages = this.getTotalPages();
    this.loadingSpinner = this.createLoadingSpinner();
    this.endMessageElement = this.createEndMessage();
    this.scrollIndicator = this.createScrollIndicator();
    this.scrollDebounceTimer = null;
    this.scrollDebounceDelay = 100; // milliseconds
    
    // Initialize if product grid exists
    if (this.productGrid) {
      this.init();
    }
  }

  init() {
    // Remove existing pagination except noscript fallback
    const paginationElement = document.querySelector('.pagination:not(noscript .pagination)');
    if (paginationElement) {
      paginationElement.remove();
    }
    
    // Add scroll event listener with debounce
    window.addEventListener('scroll', this.debouncedScrollHandler.bind(this));
    
    // Add loading spinner, scroll indicator, and end message to the container
    if (this.paginationContainer) {
      this.paginationContainer.appendChild(this.scrollIndicator);
      this.paginationContainer.appendChild(this.loadingSpinner);
      this.paginationContainer.appendChild(this.endMessageElement);
    } else if (this.productGridContainer) {
      this.productGridContainer.appendChild(this.scrollIndicator);
      this.productGridContainer.appendChild(this.loadingSpinner);
      this.productGridContainer.appendChild(this.endMessageElement);
    }
    
    // Initially hide the spinner and end message
    this.loadingSpinner.style.display = 'none';
    this.endMessageElement.style.display = 'none';
    
    // Show scroll indicator only if we have more than one page
    this.scrollIndicator.style.display = this.totalPages > 1 ? 'block' : 'none';
    
    // Check if we need to show the end message immediately (single page)
    if (this.currentPage >= this.totalPages) {
      this.endMessageElement.style.display = 'block';
      this.scrollIndicator.style.display = 'none';
    }
  }

  debouncedScrollHandler() {
    // Clear previous timeout
    clearTimeout(this.scrollDebounceTimer);
    
    // Set new timeout
    this.scrollDebounceTimer = setTimeout(() => {
      this.handleScroll();
    }, this.scrollDebounceDelay);
  }

  handleScroll() {
    // Check if we need to load more products
    if (this.isLoading || this.currentPage >= this.totalPages) return;
    
    const scrollPosition = window.innerHeight + window.scrollY;
    const bodyHeight = document.body.offsetHeight;
    const scrollThreshold = bodyHeight - (window.innerHeight * 1.5); // Load more when 1.5 viewport height from bottom
    
    if (scrollPosition >= scrollThreshold) {
      this.loadNextPage();
    }
  }

  async loadNextPage() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.loadingSpinner.style.display = 'flex';
    
    // Increment page number
    this.currentPage++;
    
    try {
      // Get the current URL and construct the next page URL
      const url = new URL(window.location.href);
      const currentPage = url.searchParams.get('page') || '1';
      url.searchParams.set('page', this.currentPage);
      
      // Update browser history to enable proper back button functionality
      if (window.history && window.history.pushState) {
        window.history.pushState({ page: this.currentPage }, '', url.toString());
      }
      
      // Fetch the next page
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const html = await response.text();
      
      // Create a temporary element to parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Get the product items from the next page
      const newProductItems = doc.querySelectorAll('#product-grid .grid__item');
      
      // Append new items to the product grid
      if (newProductItems.length > 0) {
        newProductItems.forEach(item => {
          // Clone the item to ensure all event listeners come along
          const clonedItem = item.cloneNode(true);
          this.productGrid.appendChild(clonedItem);
        });
        
        // Initialize any necessary functionality on the new elements
        this.initializeNewElements();
      }
      
      // Show end message if we've reached the last page
      if (this.currentPage >= this.totalPages) {
        this.endMessageElement.style.display = 'block';
        this.scrollIndicator.style.display = 'none';
      }
    } catch (error) {
      console.error('Error loading next page:', error);
      this.currentPage--; // Revert page number on error
    } finally {
      this.isLoading = false;
      this.loadingSpinner.style.display = this.currentPage >= this.totalPages ? 'none' : 'flex';
    }
  }

  initializeNewElements() {
    // Re-initialize card ratings (social proof) for newly appended cards
    if (typeof window.initCardRatings === 'function') {
      window.initCardRatings();
    }
    // Re-initialize quick add if it exists
    if (typeof window.initQuickAdd === 'function') {
      window.initQuickAdd();
    }
    
    // Re-initialize cards if necessary
    if (typeof window.initCards === 'function') {
      window.initCards();
    }
    
    // Initialize any card product functionality
    const newCards = document.querySelectorAll('#product-grid .card:not(.card--initialized)');
    newCards.forEach(card => {
      // Add initialized class to prevent duplicate initialization
      card.classList.add('card--initialized');
      
      // Initialize hover effects for secondary image
      const imageContainer = card.querySelector('.card__media');
      if (imageContainer) {
        const images = imageContainer.querySelectorAll('img');
        if (images.length > 1) {
          imageContainer.addEventListener('mouseenter', () => {
            images[0].style.opacity = '0';
            images[1].style.opacity = '1';
          });
          imageContainer.addEventListener('mouseleave', () => {
            images[0].style.opacity = '1';
            images[1].style.opacity = '0';
          });
        }
      }
    });
    
    // Re-init animations if theme supports it
    if (window.theme && typeof window.theme.initAnimations === 'function') {
      window.theme.initAnimations();
    }
  }

  getTotalPages() {
    // First check for data attribute in pagination container
    if (this.paginationContainer && this.paginationContainer.dataset.totalPages) {
      return parseInt(this.paginationContainer.dataset.totalPages, 10);
    }
    
    // Try to get total pages from pagination
    const pagination = document.querySelector('.pagination');
    if (pagination) {
      const paginationItems = pagination.querySelectorAll('.pagination__item');
      if (paginationItems.length > 0) {
        // Get the last number from pagination
        for (let i = paginationItems.length - 1; i >= 0; i--) {
          const text = paginationItems[i].textContent.trim();
          const parsedNum = parseInt(text, 10);
          if (!isNaN(parsedNum)) {
            return parsedNum;
          }
        }
      }
    }
    
    // Check URL for page parameter
    const urlParams = new URLSearchParams(window.location.search);
    const pageParam = urlParams.get('page');
    if (pageParam) {
      return parseInt(pageParam, 10);
    }
    
    // Fallback to 1 if we couldn't determine total pages
    return 1;
  }

  createLoadingSpinner() {
    const spinnerContainer = document.createElement('div');
    spinnerContainer.className = 'infinite-scroll-spinner';
    
    // Reuse the Dawn theme's loading spinner component
    const spinnerHTML = `
      <div class="loading-overlay__spinner">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
    
    spinnerContainer.innerHTML = spinnerHTML;
    return spinnerContainer;
  }

  createEndMessage() {
    const endMessage = document.createElement('div');
    endMessage.className = 'infinite-scroll-end-message';
    endMessage.textContent = window.theme && window.theme.strings && window.theme.strings.noMoreProducts 
      ? window.theme.strings.noMoreProducts 
      : 'No more products to load';
    return endMessage;
  }
  
  createScrollIndicator() {
    const scrollIndicator = document.createElement('div');
    scrollIndicator.className = 'infinite-scroll-indicator';
    // scrollIndicator.textContent = 'Scroll to view more';
    return scrollIndicator;
  }
}

// Initialize infinite scroll when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new EnhancedInfiniteScroll();
});

// Re-initialize on Shopify section reload events
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId.includes('main-collection-product-grid')) {
    new EnhancedInfiniteScroll();
  }
});

// Handle browser back/forward navigation
window.addEventListener('popstate', (event) => {
  if (event.state && event.state.page) {
    window.location.reload();
  }
});